/**
 * Ring a colleague, and go and wait in the room.
 *
 * NO CONFIRM STEP, deliberately. A ring is already reversible by the person it
 * costs: the colleague declines, or nobody answers and it retires itself in a
 * minute. A "call Dr Smith?" dialog would spend a click guarding against a
 * mis-selection in a list the clinician just typed a name into, and the palette
 * has no other command whose result is undone by pressing Escape.
 *
 * NOT idempotent and not debounced, unlike entering a visit's room: each call
 * PLACES a ring, so a second invocation is a second `Communication` and a
 * second room. The palette closes on select, which is the thing that stops a
 * double click, and the caller lands in a room either way.
 *
 * REFUSALS SURFACE THROUGH THE GLOBAL tRPC ERROR TOAST, as everywhere else. The
 * two that can happen are a target who is not a Practitioner and a session that
 * is not a clinician's, and neither leaves a room open: nothing is entered
 * until the server has answered.
 */

import { getTRPCClient } from "@repo/api";
import { mediaReadiness } from "@repo/conferencing/components";
import { openOutgoingCallRoom } from "./call-room";
import { prepareCallAudio } from "./call-sound-synthesis";
import { startOutgoingRingback } from "./call-sounds";

/**
 * THE CREDENTIAL `start` RETURNS IS DISCARDED, on purpose. The surface asks for
 * its own with `join` a moment later, and that is one mint rather than one
 * credential threaded from here through the call store — which holds intent
 * only and may not carry live state. `roomFor` is open-or-reuse, so the second
 * mint lands in the same room; what it costs is a round trip, and what it buys
 * is both sides of the call getting their credential the same way.
 */
export async function ringColleague(practitionerLocator: string): Promise<void> {
  // Chrome needs the context inside this gesture, not after the server replies.
  prepareCallAudio();
  void mediaReadiness.prepare();
  const startedAt = Date.now();
  try {
    const result = (await getTRPCClient().colleagueCall.start.mutate({ to: practitionerLocator })) as {
      data: { callId: string };
    };
    openOutgoingCallRoom(result.data.callId);
    startOutgoingRingback(result.data.callId, startedAt);
  } catch (error) {
    mediaReadiness.cancelPreparation();
    throw error;
  }
}
